"use client";

import { ArrowRight, Handshake } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import Link from "next/link";
import { MotionIcon } from "@/components/motion/MotionIcon";
import { cn } from "@/lib/utils";

interface DonateGoldCtaProps {
  label: string;
  href?: string;
  onClick?: () => void;
  className?: string;
  fullWidth?: boolean;
}

export function DonateGoldCta({
  label,
  href,
  onClick,
  className,
  fullWidth = false,
}: DonateGoldCtaProps) {
  const reduced = useReducedMotion();

  const classes = cn(
    "inline-flex items-center justify-center gap-2.5 rounded-full px-6 py-3.5 text-sm font-bold shadow-lg transition-shadow",
    "bg-gradient-to-r from-[var(--charity-gold)] via-[#f5d06a] to-[var(--charity-gold-dark)]",
    "text-[var(--charity-green-dark)] hover:shadow-[0_8px_28px_rgba(244,201,93,0.45)]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--charity-gold)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--charity-green-dark)]",
    fullWidth && "w-full",
    className
  );

  const content = (
    <>
      <MotionIcon icon={Handshake} size={18} hoverScale={1.15} />
      <span>{label}</span>
      <ArrowRight size={16} aria-hidden />
    </>
  );

  if (href) {
    return (
      <motion.div
        whileHover={reduced ? undefined : { y: -2, scale: 1.02 }}
        whileTap={reduced ? undefined : { scale: 0.98 }}
        className={cn("inline-flex", fullWidth && "w-full")}
      >
        <Link href={href} className={classes}>
          {content}
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={reduced ? undefined : { y: -2, scale: 1.02 }}
      whileTap={reduced ? undefined : { scale: 0.98 }}
      className={classes}
    >
      {content}
    </motion.button>
  );
}
